import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/auth";
import { useLangStore } from "../store/lang";
import { useUiStore } from "../store/ui";
import { t } from "../i18n/strings";
import { LanguagePicker } from "./LanguagePicker";
import { MobileDrawer } from "./MobileDrawer";
import { BottomTabBar } from "./BottomTabBar";
import { OfflineBanner } from "./OfflineBanner";
import { BuildBadge } from "./UpdatePrompt";
import { TrialBanner } from "./TrialBanner";
import { InstallBanner } from "./InstallBanner";
import { api } from "../lib/api";

const NAV: Array<{ to: string; key: string; label?: string }> = [
  { to: "/dashboard", key: "nav.dashboard" },
  { to: "/inbox", key: "nav.inbox", label: "AI Inbox" },
  { to: "/invoices", key: "nav.invoices" },
  { to: "/bills", key: "nav.bills" },
  { to: "/bank", key: "nav.bank" },
  { to: "/payroll", key: "nav.payroll" },
  { to: "/reports", key: "nav.reports" },
];

const SECONDARY_NAV: Array<{ to: string; label: string }> = [
  { to: "/customers", label: "Customers" },
  { to: "/suppliers", label: "Suppliers" },
  { to: "/employees", label: "Employees" },
  { to: "/team", label: "Team" },
  { to: "/spec", label: "Spec" },
  { to: "/settings", label: "Settings" },
];

export function AppShell() {
  const { user, tenant, refreshToken, clear } = useAuthStore();
  const { lang } = useLangStore();
  const { toggleDrawer } = useUiStore();
  const navigate = useNavigate();

  async function signOut() {
    if (refreshToken) {
      await api.post("/auth/logout", { refresh_token: refreshToken }).catch(() => {});
    }
    clear();
    navigate("/login", { replace: true });
  }

  const initials = (user?.name ?? "?")
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <div className="topbar">
        <div className="brand">
          <div className="brand-mark">C</div>
          ClearBooks
          <span className="brand-tag">{tenant?.name ?? ""}</span>
        </div>

        {/* Desktop / tablet pill nav — hidden ≤768px via the responsive CSS layer */}
        <div className="nav">
          {NAV.map((item) => {
            const label = t(item.key, lang);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
              >
                {label && label !== item.key ? label : (item.label ?? item.key)}
              </NavLink>
            );
          })}
        </div>

        {/* Desktop / tablet right cluster — hidden ≤768px via the responsive CSS layer */}
        <div className="topbar-right">
          {SECONDARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                isActive ? "nav-item active" : "nav-item"
              }
              style={{ padding: "6px 12px", fontSize: 12 }}
            >
              {item.label}
            </NavLink>
          ))}
          <LanguagePicker />
          <div className="user-chip">
            <div className="avatar av-1">{initials}</div>
            <span>{user?.name?.split(" ")[0]}</span>
          </div>
          <button type="button" className="btn btn-ghost" style={{ padding: "6px 12px", fontSize: 12 }} onClick={signOut}>
            {t("auth.signOut", lang)}
          </button>
          <BuildBadge />
        </div>

        {/* Hamburger — visible only ≤768px via the responsive CSS layer */}
        <button
          type="button"
          className="hamburger"
          onClick={toggleDrawer}
          aria-label="Open menu"
        >
          <span /><span /><span />
        </button>
      </div>

      <InstallBanner />
      <TrialBanner />
      <OfflineBanner />

      <div className="stage">
        <Outlet />
      </div>

      {/* Mobile-only chrome — both are CSS-gated to ≤768px */}
      <MobileDrawer />
      <BottomTabBar />
    </>
  );
}
